import React, { useEffect, useState } from 'react';

import { ADMIN_SECURITY_ROLES_FIELDS } from '../../common/fields';
import TextInput from '../Controls/TextInput/TextInput';
import Button from '../Controls/Button/Button';
import Label from '../Controls/Label/Label';
import PermissionPageField from '../Controls/PermissionsSwitches/PermissionPageSwitch';
import PermissionActionSwitch from '../Controls/PermissionsSwitches/PermissionActionSwitch';
import { DIRECTORY_TYPES, LAYOUT_TABS, ROLES, SELECTOR_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import styles from './SecurityRole.module.css';
import ADEntitiesSelector from '../../../Controls/Modals/ADEntitiesSelector/ADEntitiesSelector';
import { getUserState } from '../../../utils';
import { PricingModules, usePricingModulesState } from '../../../../context/contexts/PricingModuleContext';
import { useVisibleFields } from '../../hooks/lockedFeatures/useVisibleFields';

const {
    ROLE_NAME,
    GUIDS,
    DASHBOARD,
    POLICIES,
    SERVICE_ACCOUNTS,
    APP_DISCOVERY,
    INSIGHTS,
    USERS,
    EXCLUDE_USERS,
    DELETE_TOKENS,
    TEST_USER_TOKENS,
    SEND_ENROLLMENT_EMAIL,
    SEND_ENROLLMENT_EMAIL_TO_UNPAIRED,
    MANAGE_PROTECTED_GROUPS,
    SET_EMAIL,
    MATCH_USERS,
    DISABLE_TOKENS,
    LOGS,
    SYSTEM_STATUS,
    TOGGLE_SYSTEM_POWER,
    PREFORM_ADSYNC,
    NOTIFICATIONS,
    INVENTORY,
    INVENTORY_CLOUD,
    INVENTORY_AD,
    SOFTWARE_UPDATES,
    PRIVILEGED_ACCOUNTS,
    SETTINGS,
    AUTHORIZE,
    TEST_MFA_TOKENS,
    RISK,
    ACCESS_ANALYSIS,
    CLOUD_NHI,
    CLOUD_INTEGRATIONS,
    CLOUD_ACCESS_ANALYSIS,
    CLOUD_AI_AGENT_SECURITY,
    CLOUD_AI_ACCESS_CONTROL,
    CLOUD_INSIGHTS,
    CLOUD_RISK
} = ADMIN_SECURITY_ROLES_FIELDS;

const SecurityRole = ({ role, changedFunc }) => {
    const [userData, setUserData] = useState({});
    const [showEntitiesModal, setShowEntitiesModal] = useState(false);
    const visibleRoleFields = useVisibleFields(role);
    const { pricingModulesState } = usePricingModulesState();
    const isCloudResourcesEnabled = pricingModulesState?.[PricingModules.NHI_VISIBILITY]?.cloudEnabled;
    const isCloudInsightsEnabled = pricingModulesState?.[PricingModules.ISPM]?.cloudEnabled;
    const isCloudRiskEnabled = pricingModulesState?.[PricingModules.ITDR]?.cloudEnabled;

    const hasUserActivePage = (pageId) => {
        return !userData?.user?.hiddenPages?.includes(pageId);
    };

    const isAccessAnalysisActive = hasUserActivePage(LAYOUT_TABS.ACCESS_ANALYSIS.ID);
    const isApplicationsDiscoveryActive = hasUserActivePage(LAYOUT_TABS.APPLICATIONS_DISCOVERY.ID);

    const getNameDescription = () => {
        let nameDescription = '';

        const allNames = visibleRoleFields[GUIDS.ID].value
            .filter((se) => se !== null && se !== undefined)
            .map((se) => {
                if (se.is_deleted) {
                    return se.name + ' (deleted)';
                }

                return se.name;
            });

        if (allNames.length > 0) {
            nameDescription = allNames.slice(0, 2).join(', ');
            if (allNames.length > 2) {
                nameDescription += ` and ${allNames.length - 2} more`;
            }
        }

        return nameDescription;
    };

    const onSaveEntitiesClicked = (newEntities) => {
        changedFunc(GUIDS.ID, newEntities.objectEntities);
        setShowEntitiesModal(false);
    };

    const switchChangeFunc = (fId, newVal) => {
        if (newVal === null || visibleRoleFields[fId].value === newVal) {
            return;
        }
        changedFunc(fId, newVal);
        if (fId === SETTINGS.ID) {
            changedFunc(AUTHORIZE.ID, getActionNewValue(newVal));
            changedFunc(TEST_MFA_TOKENS.ID, getActionNewValue(newVal));
            changedFunc(CLOUD_INTEGRATIONS.ID, getActionNewValue(newVal));
        }
        if (fId === USERS.ID) {
            changedFunc(EXCLUDE_USERS.ID, getActionNewValue(newVal));
            changedFunc(DELETE_TOKENS.ID, getActionNewValue(newVal));
            changedFunc(TEST_USER_TOKENS.ID, getActionNewValue(newVal));
            changedFunc(SEND_ENROLLMENT_EMAIL.ID, getActionNewValue(newVal));
            changedFunc(SEND_ENROLLMENT_EMAIL_TO_UNPAIRED.ID, getActionNewValue(newVal));
            changedFunc(MANAGE_PROTECTED_GROUPS.ID, getActionNewValue(newVal));
            changedFunc(SET_EMAIL.ID, getActionNewValue(newVal));
            changedFunc(MATCH_USERS.ID, getActionNewValue(newVal));
            changedFunc(DISABLE_TOKENS.ID, getActionNewValue(newVal));
        }
        if (fId === SYSTEM_STATUS.ID) {
            changedFunc(TOGGLE_SYSTEM_POWER.ID, getActionNewValue(newVal));
            changedFunc(PREFORM_ADSYNC.ID, getActionNewValue(newVal));
        }
    };

    const getActionNewValue = (pageNewVal) => {
        return pageNewVal === ROLES.EDITOR.value ? ROLES.EDITOR.value : ROLES.NONE.value;
    };

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const userData = await getUserState();
                setUserData(userData);
            } catch (error) {
                console.error("failed getting the user's data from server!", error);
            }
        };
        fetchUserData();
    }, []);
    return (
        <div className={styles.securityRole}>
            <TextInput field={visibleRoleFields[ROLE_NAME.ID]} color="grey" changed={changedFunc} />
            <TextInput
                field={{ ...visibleRoleFields[GUIDS.ID], value: getNameDescription() }}
                color="grey"
                disabled={true}
                style={{ maxWidth: '350px' }}>
                <Button
                    onClick={() => {
                        setShowEntitiesModal(true);
                    }}>
                    Edit...
                </Button>
            </TextInput>
            <Label value={'Permissions'} />
            <div className={styles.permissions}>
                <div>
                    <PermissionPageField field={visibleRoleFields[DASHBOARD.ID]} changed={switchChangeFunc} />
                    <PermissionPageField field={visibleRoleFields[POLICIES.ID]} changed={switchChangeFunc} />
                    <PermissionPageField field={visibleRoleFields[SERVICE_ACCOUNTS.ID]} changed={switchChangeFunc} />
                    {isCloudResourcesEnabled && (
                        <PermissionPageField field={visibleRoleFields[CLOUD_NHI.ID]} changed={switchChangeFunc} />
                    )}
                    {isApplicationsDiscoveryActive && (
                        <PermissionPageField field={visibleRoleFields[APP_DISCOVERY.ID]} changed={switchChangeFunc} />
                    )}
                    {isAccessAnalysisActive && (
                        <>
                            <PermissionPageField
                                field={visibleRoleFields[ACCESS_ANALYSIS.ID]}
                                changed={switchChangeFunc}
                            />
                            <PermissionPageField
                                field={visibleRoleFields[CLOUD_ACCESS_ANALYSIS.ID]}
                                changed={switchChangeFunc}
                            />
                        </>
                    )}
                    <PermissionPageField field={visibleRoleFields[INSIGHTS.ID]} changed={switchChangeFunc} />

                    {isCloudInsightsEnabled && (
                        <PermissionPageField field={visibleRoleFields[CLOUD_INSIGHTS.ID]} changed={switchChangeFunc} />
                    )}
                    <PermissionPageField field={visibleRoleFields[RISK.ID]} changed={switchChangeFunc} />
                    {isCloudRiskEnabled && (
                        <PermissionPageField field={visibleRoleFields[CLOUD_RISK.ID]} changed={switchChangeFunc} />
                    )}
                    <PermissionPageField field={visibleRoleFields[LOGS.ID]} changed={switchChangeFunc} />

                    <PermissionPageField
                        field={visibleRoleFields[CLOUD_AI_AGENT_SECURITY.ID]}
                        changed={switchChangeFunc}
                    />
                    <PermissionPageField
                        field={visibleRoleFields[CLOUD_AI_ACCESS_CONTROL.ID]}
                        changed={switchChangeFunc}
                    />
                    <PermissionPageField field={visibleRoleFields[SETTINGS.ID]} changed={switchChangeFunc}>
                        {visibleRoleFields[AUTHORIZE.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[AUTHORIZE.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[SETTINGS.ID].value === ROLES.NONE.value}
                            />
                        )}
                        {visibleRoleFields[TEST_MFA_TOKENS.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[TEST_MFA_TOKENS.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[SETTINGS.ID].value === ROLES.NONE.value}
                            />
                        )}
                        {visibleRoleFields[CLOUD_INTEGRATIONS.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[CLOUD_INTEGRATIONS.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[SETTINGS.ID].value === ROLES.NONE.value}
                            />
                        )}
                    </PermissionPageField>
                </div>
                <div>
                    <PermissionPageField field={visibleRoleFields[USERS.ID]} changed={switchChangeFunc}>
                        {visibleRoleFields[EXCLUDE_USERS.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[EXCLUDE_USERS.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[USERS.ID]?.value === ROLES.NONE.value}
                            />
                        )}
                        {visibleRoleFields[DELETE_TOKENS.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[DELETE_TOKENS.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[USERS.ID]?.value === ROLES.NONE.value}
                            />
                        )}
                        {visibleRoleFields[TEST_USER_TOKENS.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[TEST_USER_TOKENS.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[USERS.ID]?.value === ROLES.NONE.value}
                            />
                        )}
                        {visibleRoleFields[SEND_ENROLLMENT_EMAIL.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[SEND_ENROLLMENT_EMAIL.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[USERS.ID]?.value === ROLES.NONE.value}
                            />
                        )}
                        {visibleRoleFields[SEND_ENROLLMENT_EMAIL_TO_UNPAIRED.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[SEND_ENROLLMENT_EMAIL_TO_UNPAIRED.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[USERS.ID]?.value === ROLES.NONE.value}
                            />
                        )}
                        {visibleRoleFields[MANAGE_PROTECTED_GROUPS.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[MANAGE_PROTECTED_GROUPS.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[USERS.ID]?.value === ROLES.NONE.value}
                            />
                        )}
                        {visibleRoleFields[SET_EMAIL.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[SET_EMAIL.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[USERS.ID]?.value === ROLES.NONE.value}
                            />
                        )}
                        {visibleRoleFields[MATCH_USERS.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[MATCH_USERS.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[USERS.ID]?.value === ROLES.NONE.value}
                            />
                        )}
                        {visibleRoleFields[DISABLE_TOKENS.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[DISABLE_TOKENS.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[USERS.ID]?.value === ROLES.NONE.value}
                            />
                        )}
                    </PermissionPageField>
                    <PermissionPageField field={visibleRoleFields[SYSTEM_STATUS.ID]} changed={switchChangeFunc}>
                        {visibleRoleFields[TOGGLE_SYSTEM_POWER.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[TOGGLE_SYSTEM_POWER.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[SYSTEM_STATUS.ID]?.value === ROLES.NONE.value}
                            />
                        )}
                        {visibleRoleFields[PREFORM_ADSYNC.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[PREFORM_ADSYNC.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[SYSTEM_STATUS.ID]?.value === ROLES.NONE.value}
                            />
                        )}
                    </PermissionPageField>
                    <PermissionPageField field={visibleRoleFields[NOTIFICATIONS.ID]} changed={switchChangeFunc} />
                    <PermissionPageField field={visibleRoleFields[SOFTWARE_UPDATES.ID]} changed={switchChangeFunc} />
                    <PermissionPageField field={visibleRoleFields[PRIVILEGED_ACCOUNTS.ID]} changed={switchChangeFunc} />
                    <PermissionPageField field={visibleRoleFields[INVENTORY.ID]} changed={switchChangeFunc}>
                        {visibleRoleFields[INVENTORY_AD.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[INVENTORY_AD.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[INVENTORY.ID].value === ROLES.NONE.value}
                            />
                        )}
                        {visibleRoleFields[INVENTORY_CLOUD.ID] && (
                            <PermissionActionSwitch
                                field={visibleRoleFields[INVENTORY_CLOUD.ID]}
                                changed={switchChangeFunc}
                                disabled={visibleRoleFields[INVENTORY.ID].value === ROLES.NONE.value}
                            />
                        )}
                    </PermissionPageField>
                </div>
            </div>

            {showEntitiesModal && (
                <ADEntitiesSelector
                    selectedObjectEntities={visibleRoleFields[GUIDS.ID].value}
                    selectorType={SELECTOR_TYPES.ENTITIES}
                    showModal={showEntitiesModal}
                    handleCloseModal={() => {
                        setShowEntitiesModal(false);
                    }}
                    saveEntities={onSaveEntitiesClicked}
                    showExcluded={false}
                    authTypes={[DIRECTORY_TYPES.ad.key]}
                />
            )}
        </div>
    );
};

export default React.memo(SecurityRole);
